/**
 * Profile header — cinematic identity banner at the top of the profile page.
 *
 * Layered backdrop (cream gradient + radial gold/teal blooms + faint dot grid)
 * with a 128px avatar, serif display name, role badges, contact line,
 * verification rail, and a 3-up stat strip at the bottom (Member Since /
 * Last Login / Account Status). Admins get a teal "ComplyV Staff" chip
 * next to the role pill.
 *
 * Pure presentation; data comes from the live profile fetched by ProfilePage.
 */

import { Pill } from './ProfilePrimitives';
import Icon from '../common/Icon';

const BORDER = '#ddd6c6';
const TEXT = '#08111f';
const MUTED = '#7a7060';
const TILE_BORDER = '#e8dec3';

const ROLE_LABELS = {
  professional: 'CA / CS Professional',
  corporate: 'Corporate',
  admin: 'Administrator',
  super_admin: 'Super Admin',
};

const STAFF_ROLES = new Set(['admin', 'super_admin']);

const KYC_TONE = {
  verified: 'green',
  pending: 'orange',
  rejected: 'red',
  not_submitted: 'neutral',
};

function getInitials(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'PS';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Date + time — used for "Last Login", where the exact moment matters.
function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  // Uppercase the am/pm so it reads "2:45 PM" (en-IN renders it lowercase).
  const time = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true }).toUpperCase();
  return `${date}, ${time}`;
}

function StatTile({ icon, label, value }) {
  return (
    <div
      className="flex items-center gap-3 px-4 py-[12px] rounded-[12px]"
      style={{
        background: 'rgba(255,255,255,.72)',
        border: `1px solid ${TILE_BORDER}`,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        className="w-[36px] h-[36px] rounded-[10px] flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(184,151,58,.14)', color: '#8a6a1f' }}
      >
        <Icon name={icon} size={17} />
      </div>
      <div className="min-w-0">
        <div className="text-[9.5px] font-bold uppercase tracking-[1.4px]" style={{ color: MUTED }}>
          {label}
        </div>
        <div className="mt-[2px] text-[13.5px] font-semibold truncate" style={{ color: TEXT }}>
          {value}
        </div>
      </div>
    </div>
  );
}

export default function ProfileHeaderCard({ user }) {
  const roleName = String(user?.roleName || '').toLowerCase();
  const roleLabel = ROLE_LABELS[roleName] || 'User';
  const isStaff = STAFF_ROLES.has(roleName);
  const lastLogin = formatDateTime(user?.lastLogin);
  const memberSince = formatDate(user?.createdAt);
  const kyc = user?.kycStatus || 'not_submitted';
  const isActive = user?.isActive !== false;

  return (
    <section
      className="relative rounded-[18px] border overflow-hidden mb-7"
      style={{
        borderColor: BORDER,
        background: 'linear-gradient(180deg,#fdfbf6 0%,#fff8eb 36%,#ffffff 100%)',
        boxShadow: '0 10px 36px rgba(8,17,31,0.08)',
      }}
    >
      {/* Top hairline — gold to teal, sits above all decorative layers */}
      <div
        className="h-[3px] w-full relative z-10"
        style={{ background: 'linear-gradient(90deg,#b8973a 0%,#1a7373 100%)' }}
      />

      {/* Decorative layer 1 — soft gold + teal radial blooms behind content */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background: `
            radial-gradient(circle at 14% 32%, rgba(184,151,58,.14) 0%, transparent 40%),
            radial-gradient(circle at 86% 80%, rgba(20,95,95,.10) 0%, transparent 42%)
          `,
        }}
      />

      {/* Decorative layer 2 — faint dot grid for texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(8,17,31,.06) 1px, transparent 1px)',
          backgroundSize: '24px 24px',
          opacity: 0.5,
          mixBlendMode: 'multiply',
        }}
      />

      {/* Content */}
      <div className="relative px-7 pt-8 pb-6 sm:px-9 sm:pt-9 sm:pb-7">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-7">
          {/* Avatar — 128px gradient circle, gold accent ring, deep halo */}
          <div
            className="w-[128px] h-[128px] rounded-full flex items-center justify-center text-[38px] font-black text-white flex-shrink-0 overflow-hidden"
            style={{
              background: 'linear-gradient(135deg,#b8973a 0%,#1e8a8a 100%)',
              boxShadow: '0 0 0 4px #ffffff, 0 0 0 6px rgba(184,151,58,.65), 0 14px 36px rgba(20,95,95,.24)',
              letterSpacing: '.5px',
            }}
          >
            {user?.profilePicture ? (
              <img src={user.profilePicture} alt={user.fullName} className="w-full h-full object-cover" />
            ) : (
              getInitials(user?.fullName)
            )}
          </div>

          {/* Identity column */}
          <div className="min-w-0 flex-1">
            <h2
              className="m-0 text-[32px] sm:text-[36px] font-bold leading-[1.1] mb-2"
              style={{
                color: TEXT,
                fontFamily: "'Cormorant Garamond', serif",
                letterSpacing: '.3px',
              }}
            >
              {user?.fullName || 'Unnamed User'}
            </h2>

            <div className="flex items-center gap-2 flex-wrap mb-[10px]">
              <Pill tone="gold">{roleLabel}</Pill>
              {isStaff && <Pill tone="teal"><Icon name="shield" size={11} /> ComplyV Staff</Pill>}
              <Pill tone={isActive ? 'green' : 'red'}>
                {isActive ? '● Active' : '● Inactive'}
              </Pill>
            </div>

            <div className="text-[13px] flex items-center gap-[6px] flex-wrap mb-[14px]" style={{ color: MUTED }}>
              <span>{user?.email}</span>
              {user?.mobile && (
                <>
                  <span style={{ color: '#c8bda6' }}>·</span>
                  <span>+91 {user.mobile}</span>
                </>
              )}
            </div>

            {/* Verification rail */}
            <div className="flex items-center gap-2 flex-wrap">
              <Pill tone={user?.isEmailVerified ? 'green' : 'neutral'}>
                {user?.isEmailVerified ? '✓ Email verified' : '! Email unverified'}
              </Pill>
              <Pill tone={user?.isPhoneVerified ? 'green' : 'neutral'}>
                {user?.isPhoneVerified ? '✓ Phone verified' : '! Phone unverified'}
              </Pill>
              <Pill tone={KYC_TONE[kyc] || 'neutral'}>
                KYC: {String(kyc).replace('_', ' ')}
              </Pill>
            </div>
          </div>
        </div>

        {/* Bottom stats strip — separator + 3-up grid */}
        <div
          className="mt-7 pt-[22px] border-t border-dashed"
          style={{ borderColor: '#e8dec3' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatTile icon="📅" label="Member Since" value={memberSince || '—'} />
            <StatTile icon="🕐" label="Last Login" value={lastLogin || 'Never'} />
            <StatTile
              icon={isActive ? '⚡' : '⏸'}
              label="Account Status"
              value={isActive ? 'Active' : 'Inactive'}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
